// Shared date-only formatter — every full calendar-date display in the app
// goes through this one function (imported as fmtDMY in ~20 files), so
// changing the output format here changes it everywhere at once. Built by
// hand instead of toLocaleDateString(locale, opts) because the locale-based
// approach depends on the browser/OS's own locale settings and doesn't
// reliably give the same shape across environments.
//
// Current output: ordinal day + short month + year, e.g. "22nd Sep 2026".
// (The name fmtDMY is historical from an earlier DD-MM-YYYY format — kept
// as-is rather than renaming across every call site for a formatting-only
// change.)
//
// Only for genuine calendar-date displays (joined date, last working day,
// resignation date, etc.) — NOT for:
//   - <input type="date"> (the browser controls that picker's display format
//     itself; this function can't and shouldn't touch it)
//   - short recurring-date badges that deliberately omit the year (e.g. an
//     upcoming birthday shown as "22 Sept")
//   - relative/"time ago" labels
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function ordinal(n) {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

export function fmtDMY(input) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return `${ordinal(d.getDate())} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

// DD-MM-YY for ISO dates shown as plain text (e.g. "2026-10-05" → "05-10-26").
// Reads the parts straight from the string so a date-only value never shifts
// a day by timezone. Non-ISO input is returned unchanged.
export function fmtDDMMYY(input) {
  if (!input) return "";
  const s = String(input);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  return `${m[3]}-${m[2]}-${m[1].slice(2)}`;
}

// Any stored value that might be a date or a timestamp, made readable — for places that show raw data
// (the activity feed's "what changed" lines). Returns the value untouched when it isn't one.
//   "2026-10-08"                → "8th Oct 2026"
//   "2017-10-23T12:00:00.000Z"  → "23rd Oct 2017"   (the app stores plain dates as noon UTC)
//   "2026-10-08T09:53:00"       → "8th Oct 2026, 9:53 AM"   (wall-clock time, shown as it was entered)
//   "2026-10-08T04:23:00.000Z"  → "8th Oct 2026, 9:53 AM"   (a real instant, shown in India time)
export function fmtAnyDate(v) {
  if (typeof v !== "string") return v;
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return fmtDMY(new Date(+m[1], +m[2] - 1, +m[3]));
  m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/.exec(v);
  if (!m) return v;
  const [, y, mo, d, hh, mi, ss, zone] = m;
  if (zone === "Z" && hh === "12" && mi === "00" && (!ss || ss === "00")) return fmtDMY(new Date(+y, +mo - 1, +d)); // date-only column
  let Y = +y, M = +mo, D = +d, H = +hh, I = +mi;
  if (zone) {
    const t = new Date(v);
    if (Number.isNaN(t.getTime())) return v;
    const p = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", hourCycle: "h23" })
      .formatToParts(t).reduce((a, x) => (a[x.type] = x.value, a), {});
    Y = +p.year; M = +p.month; D = +p.day; H = +p.hour; I = +p.minute;
  }
  const ampm = H >= 12 ? "PM" : "AM";
  const h12 = H % 12 === 0 ? 12 : H % 12;
  return `${fmtDMY(new Date(Y, M - 1, D))}, ${h12}:${String(I).padStart(2, "0")} ${ampm}`;
}
